import axios, { type AxiosInstance } from 'axios';

/**
 * The forms app never has direct credentials. The host injects an access
 * token through the bridge (`init` / `token:update`). This module stores the
 * latest known token and stamps it onto every outgoing request, mirroring
 * the interceptor pattern used in lum-web (`src/utils/network-service.js`).
 *
 * It also stores any deployment-context headers the host supplies via
 * `init.headers` (e.g. `x-customerid`, `marketplacekey`, `x-solution`) and
 * merges them into every request, so flow code never has to know they
 * exist.
 */

let currentToken: string | null = null;
let currentHeaders: Record<string, string> = {};

export const setAuthToken = (token: string | null): void => {
  currentToken = token;
};

export const getAuthToken = (): string | null => currentToken;

export const setApiHeaders = (headers: Record<string, string> | null): void => {
  currentHeaders = headers ?? {};
};

export const getApiHeaders = (): Record<string, string> => currentHeaders;

export const createApiClient = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use((config) => {
    config.headers = config.headers ?? {};
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    for (const [name, value] of Object.entries(currentHeaders)) {
      config.headers[name] = value;
    }
    return config;
  });

  return instance;
};
